import { useMemo, useEffect } from 'react';
import { format } from 'date-fns';
import { LoadingDots, Paragraph } from '@expressable/syntax-ds';
import { DayOfWeekPicker } from 'components/DayOfWeekPicker/DayOfWeekPicker';
import { TimeZone, TimezoneOption } from 'domains/timezone';
import { useCalendarAvailableTimes } from 'domains/calendars/hooks';
import { TimeSlotPickerV2 } from 'components/TimeSlotPickerV2/TimeSlotPickerV2';
import { useTranslation } from 'react-i18next';

// TYPES ------------------------------------
type Props = {
  evalTimeSlot: Date;
  displayTimezone?: TimezoneOption;
  selectedDay?: number;
  disabledDay?: number;
  timeSlot?: Date;
  calendarID?: string;
  isSecondAppointment?: boolean;
  isSubmitting: boolean;
  billingType?: string;
  onDayChange: (day: number) => void;
  onTimeSlotChange: (timeSlot?: Date, therapistName?: string) => void;
  onSubmit: (value: Date, displayTimezone: TimeZone, calendarID?: number) => void;
};

const PreBookSchedulePicker: React.FC<Props> = ({
  evalTimeSlot,
  displayTimezone,
  selectedDay,
  disabledDay,
  timeSlot,
  calendarID,
  isSecondAppointment,
  isSubmitting,
  onDayChange,
  onTimeSlotChange,
  onSubmit,
}) => {
  // CONSTS ------------------------------------
  const { t } = useTranslation();
  const DATE_OFFSET = 7; // billingType === 'privatePay' ? 3 : 7; // Can be adjusted in the future.

  // UTILS ------------------------------------
  const getNextDayOccurrence = (dayOfWeek: number | undefined): Date => {
    if (dayOfWeek === undefined) return new Date();

    const result = new Date(evalTimeSlot);
    result.setDate(result.getDate() - result.getDay() + dayOfWeek + (dayOfWeek === 0 ? 7 : 0));
    return result;
  };

  useEffect(() => {
    if (selectedDay !== undefined) {
      onTimeSlotChange(undefined);
    }
  }, [selectedDay]);

  // MEMOIZED VALUES -------------------------
  const fromDate = useMemo(() => getNextDayOccurrence(selectedDay), [selectedDay]);

  const displayDate = useMemo(() => {
    const date = new Date(fromDate);
    date.setDate(date.getDate() + DATE_OFFSET);

    // Calculate the difference in days
    const diffTime = date.getTime() - new Date(evalTimeSlot).getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // If difference is less than N days, add another week
    if (diffDays < DATE_OFFSET) {
      date.setDate(date.getDate() + DATE_OFFSET);
    }

    return date;
  }, [fromDate, calendarID, evalTimeSlot]);

  // QUERIES --------------------------------
  const { data: newTimeSlotData, isLoading: isAvailabilityLoading } = useCalendarAvailableTimes({
    calendarID: calendarID || '',
    date: format(displayDate, 'yyyy-MM-dd'),
  });

  // DERIVED STATE --------------------------
  const availableTimeSlots = useMemo(() => {
    if (!newTimeSlotData?.items) return [];

    const availableSlots = newTimeSlotData.items
      .filter(item => item.isAvailableNextWeek && !item.conflicts?.length)
      .map(item => new Date(item.time));

    // If there's a selected time slot, check if it's still available
    if (timeSlot && !availableSlots.some(slot => slot.getTime() === timeSlot.getTime())) {
      onTimeSlotChange(undefined);
    }

    return availableSlots;
  }, [newTimeSlotData, timeSlot, onTimeSlotChange]);

  const selectedTimeSlot = useMemo(() => {
    if (!timeSlot) return undefined;
    return availableTimeSlots.find(slot => slot.getTime() === timeSlot.getTime());
  }, [timeSlot, availableTimeSlots]);

  // HANDLERS ------------------------------
  const handleTimeSlotChange = (newTimeSlot: Date) => {
    const therapistName = newTimeSlotData?.therapistName;

    onTimeSlotChange(newTimeSlot, therapistName);
    if (displayTimezone) {
      onSubmit(newTimeSlot, displayTimezone.value, Number(calendarID));
    }
  };

  // RENDER -------------------------------
  return (
    <>
      {displayTimezone && (
        <div>
          <DayOfWeekPicker
            value={selectedDay}
            settings={{ isSecond: !!isSecondAppointment, disabledDay }}
            onChange={onDayChange}
            labelKey={t('signup.direct.steps.prebook.scheduler.dayPickerLabel')}
            disabled={isSubmitting}
          />

          {selectedDay !== undefined && (
            <>
              {isAvailabilityLoading ? (
                <LoadingDots
                  className="flex flex-col h-max static mt-48"
                  message={t('signup.direct.steps.prebook.scheduler.loading', {
                    date: format(displayDate, "EEEE',' MMMM d"),
                  })}
                />
              ) : availableTimeSlots.length > 0 ? (
                <div className="mt-48">
                  <TimeSlotPickerV2
                    onChange={handleTimeSlotChange}
                    timeSlots={availableTimeSlots}
                    value={selectedTimeSlot}
                    labelKey={t('signup.direct.steps.prebook.scheduler.timePickerLabel')}
                    disabled={isSubmitting}
                    timezone={displayTimezone?.value}
                  />
                </div>
              ) : (
                <Paragraph className="mt-56">{t('signup.direct.steps.prebook.scheduler.noSlots')}</Paragraph>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default PreBookSchedulePicker;
